import { useState, useCallback } from 'react';
import { message } from 'antd';
import { updateSpreadsheetCells } from '../utils/helpers';

export const useSpreadsheetClipboard = ({
  // State dependencies
  selectionStart,
  selectionEnd,
  activeCell,
  filteredData,
  data,
  currentSheetType,
  year,
  
  // Utility functions
  getVisibleColumns,
  updateCells,
}) => {
  const [copiedCells, setCopiedCells] = useState(null);
  const [copiedCellsRange, setCopiedCellsRange] = useState(null);

  const copySelectedCells = useCallback(() => {
    if (!selectionStart || !selectionEnd || filteredData.length === 0) return;
    const [startRow, startCol] = selectionStart;
    const [endRow, endCol] = selectionEnd;
    const minRow = Math.max(0, Math.min(startRow, endRow));
    const maxRow = Math.min(filteredData.length - 1, Math.max(startRow, endRow));
    const minCol = Math.min(startCol, endCol);
    const maxCol = Math.max(startCol, endCol);

    const visibleCols = Object.keys(getVisibleColumns());
    const copiedData = [];
    for (let i = minRow; i <= maxRow; i++) {
      const row = [];
      for (let j = minCol; j <= maxCol; j++) {
        const columnKey = visibleCols[j];
        row.push(filteredData[i][columnKey]);
      }
      copiedData.push(row);
    }

    setCopiedCells({
      data: copiedData,
      rowCount: maxRow - minRow + 1,
      colCount: maxCol - minCol + 1,
    });

    const clipboardText = copiedData.map((row) => row.join('\t')).join('\n');
    navigator.clipboard.writeText(clipboardText);

    setCopiedCellsRange({ minRow, maxRow, minCol, maxCol });
  }, [selectionStart, selectionEnd, filteredData, getVisibleColumns]);

  const pasteSelectedCells = useCallback(async () => {
    if (activeCell && copiedCells && copiedCellsRange) {
      const [startRow, startCol] = activeCell;
      const { data: copiedData, rowCount, colCount } = copiedCells;
      const { minCol: copiedStartCol } = copiedCellsRange;
      
      const visibleColumns = Object.keys(getVisibleColumns());
      
      const targetColumns = Array.from({ length: colCount }, (_, i) => startCol + i);
      const sourceColumns = Array.from({ length: colCount }, (_, i) => copiedStartCol + i);
      
      const isColumnMismatch = targetColumns.some((targetCol, index) => {
        const sourceCol = sourceColumns[index];
        const targetColumnKey = visibleColumns[targetCol];
        const sourceColumnKey = visibleColumns[sourceCol];
        return targetColumnKey !== sourceColumnKey;
      });
      
      if (isColumnMismatch) {
        message.error('Cannot paste data between different columns. Please paste within the same column type.');
        return;
      }
      
      const cellsToUpdate = [];
      
      for (let i = 0; i < rowCount; i++) {
        const targetRow = startRow + i;
        if (targetRow >= filteredData.length) continue;
        const rowData = filteredData[targetRow];
        
        for (let j = 0; j < colCount; j++) {
          const targetCol = startCol + j;
          if (targetCol < visibleColumns.length) {
            const columnKey = visibleColumns[targetCol];
            cellsToUpdate.push({
              unid: rowData.unid,
              columnId: columnKey,
              value: copiedData[i][j]
            });
          }
        }
      }
      
      try {
        // Use the React Query mutation
        updateCells(cellsToUpdate);
      } catch (error) {
        console.error('Error updating cells:', error);
        message.error('Failed to update cells: ' + error.message);
      }
    }
  }, [activeCell, copiedCells, copiedCellsRange, filteredData, data, getVisibleColumns, currentSheetType, year, updateCells]);

  const cutSelectedCells = useCallback(() => {
    if (selectionStart && selectionEnd) {
      copySelectedCells();
      
      const [startRow, startCol] = selectionStart;
      const [endRow, endCol] = selectionEnd;
      const minRow = Math.min(startRow, endRow);
      const maxRow = Math.max(startRow, endRow);
      const minCol = Math.min(startCol, endCol);
      const maxCol = Math.max(startCol, endCol);
      
      // Prepare cells to update
      const cellsToUpdate = [];
      const visibleColumns = Object.keys(getVisibleColumns());
      for (let i = minRow; i <= maxRow; i++) {
        const rowData = filteredData[i];
        visibleColumns.forEach((key, colIndex) => {
          if (colIndex >= minCol && colIndex <= maxCol) {
            cellsToUpdate.push({
              unid: rowData.unid,
              columnId: key,
              value: ''
            });
          }
        });
      }
      
      // Use the React Query mutation
      updateCells(cellsToUpdate);
      
      setCopiedCellsRange({ minRow, maxRow, minCol, maxCol });
    }
  }, [selectionStart, selectionEnd, filteredData, getVisibleColumns, copySelectedCells, updateCells]);

  return {
    copiedCells,
    copiedCellsRange,
    copySelectedCells,
    pasteSelectedCells,
    cutSelectedCells,
  };
};